import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, ShieldCheck, Truck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import { logger } from "@/utils/logger";

const money = (cents = 0) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD",
}).format(Number(cents));

const labels = {
  captured: "Payment protected",
  delivery_reported: "Delivery reported",
  disputed: "Dispute under review",
  released: "Seller paid",
  completed: "Complete",
  refunded: "Buyer refunded",
};

export const AuctionPaymentPanel = ({ auctionId, user }) => {
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/auctions/${auctionId}/payment-status`);
      setPayment(data);
    } catch (err) {
      if (![403, 404].includes(err.response?.status)) logger.error(err);
      setPayment(null);
    } finally {
      setLoading(false);
    }
  }, [auctionId]);

  useEffect(() => { refresh(); }, [refresh]);

  const act = async (name, endpoint, body, success) => {
    setWorking(name);
    try {
      await api.post(endpoint, body);
      toast.success(success);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "The auction payment could not be updated.");
    } finally {
      setWorking("");
    }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#D4AF37]" /></div>;
  if (!payment) return null;

  const userId = user?.user_id;
  const isBuyer = userId === payment.buyer_user_id;
  const isSeller = userId === payment.seller_user_id;
  const canDispute = ["captured", "delivery_reported"].includes(payment.status);
  const releaseAt = payment.automatic_release_at ? new Date(payment.automatic_release_at).toLocaleString() : null;

  const openDispute = () => {
    const reason = window.prompt("Briefly explain the problem. Breedling will hold the seller payout while it is reviewed.");
    if (!reason) return;
    if (reason.trim().length < 10) {
      toast.error("Please provide at least 10 characters.");
      return;
    }
    act("dispute", `/auctions/${auctionId}/dispute`, { reason: reason.trim() }, "The payout is on hold while Breedling reviews the dispute.");
  };

  return (
    <section className="mt-6 rounded-2xl border border-[#D4AF37]/30 bg-[#0A0A0A] p-5" data-testid="auction-payment-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[#D4AF37]">
            <ShieldCheck className="w-5 h-5" />
            <h2 className="text-lg text-[#EDEDED]">Protected Payment</h2>
          </div>
          <p className="text-sm text-[#A1A1AA] mt-1">
            Breedling holds the payment in its Stripe platform balance until delivery is confirmed or the review window ends.
          </p>
        </div>
        <span className="rounded-full bg-[#D4AF37]/10 px-3 py-1 text-xs text-[#D4AF37]">
          {labels[payment.status] || payment.status}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
        <div className="rounded-lg bg-white/5 p-3">
          <p className="text-[#71717A]">Winning bid</p>
          <p className="text-[#EDEDED]">{money(payment.winning_bid)}</p>
        </div>
        <div className="rounded-lg bg-white/5 p-3">
          <p className="text-[#71717A]">{isBuyer ? "Your total" : "Seller payout"}</p>
          <p className="text-[#EDEDED]">{money(isBuyer ? payment.buyer_total : payment.seller_payout_amount)}</p>
        </div>
      </div>

      {payment.status === "delivery_reported" && releaseAt && (
        <p className="mt-4 text-sm text-[#A1A1AA]">
          If no dispute is opened, payout is scheduled after {releaseAt}.
        </p>
      )}
      {payment.status === "disputed" && (
        <div className="mt-4 flex gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          Seller payout is paused while Breedling reviews this transaction.
        </div>
      )}

      <div className="flex flex-wrap gap-3 mt-5">
        {isSeller && payment.status === "captured" && (
          <button
            onClick={() => window.confirm("Confirm that delivery has been completed? This starts the 72-hour buyer review window.") &&
              act("delivery", `/auctions/${auctionId}/mark-delivered`, {}, "Delivery reported. The buyer has been notified.")}
            disabled={!!working}
            className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-black disabled:opacity-50"
          >
            {working === "delivery" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
            Report Delivery
          </button>
        )}
        {isBuyer && payment.status === "delivery_reported" && (
          <button
            onClick={() => window.confirm("Confirm you received the animal or service as agreed? This releases the seller payout.") &&
              act("receipt", `/auctions/${auctionId}/confirm-receipt`, {}, "Receipt confirmed. The seller payout has been released.")}
            disabled={!!working}
            className="inline-flex items-center gap-2 rounded-full bg-[#22C55E] px-5 py-2.5 text-black disabled:opacity-50"
          >
            {working === "receipt" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Confirm Receipt
          </button>
        )}
        {(isBuyer || isSeller) && canDispute && (
          <button
            onClick={openDispute}
            disabled={!!working}
            className="inline-flex items-center gap-2 rounded-full border border-red-500/50 px-5 py-2.5 text-red-300 disabled:opacity-50"
          >
            {working === "dispute" ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
            Report a Problem
          </button>
        )}
      </div>
    </section>
  );
};

export default AuctionPaymentPanel;
